import { IFavourite, IFavouriteMutation } from "@eventr/common";

/** Favourites keyed by entry id. Serialises to JSON, and looks up in constant time. */
export type FavouriteMap = Record<string, IFavourite>;

export const toFavouriteMap = (favourites: IFavourite[]): FavouriteMap =>
  favourites.reduce<FavouriteMap>((map, favourite) => {
    map[favourite.entryId] = favourite;
    return map;
  }, {});

export const isFavourited = (map: FavouriteMap, entryId: string): boolean => map[entryId] !== undefined;

/**
 * Applies queued changes, newest `at` winning per entry. Timestamps are ISO 8601 in UTC,
 * which sorts as text.
 */
export const applyFavouriteMutations = (map: FavouriteMap, mutations: IFavouriteMutation[]): FavouriteMap => {
  const next: FavouriteMap = { ...map };
  const appliedAt: Record<string, string> = {};

  for (const { entryId, itineraryId, favourited, at } of mutations) {
    const seen = appliedAt[entryId] ?? next[entryId]?.favouritedAt;
    if (seen !== undefined && seen > at) continue;

    appliedAt[entryId] = at;

    if (favourited) {
      next[entryId] = { entryId, itineraryId, favouritedAt: at };
    } else {
      delete next[entryId];
    }
  }

  return next;
};

/** Folds the server's answer together with changes made while the request was in flight. */
export const reconcileFavourites = (server: IFavourite[], stillQueued: IFavouriteMutation[]): FavouriteMap =>
  applyFavouriteMutations(toFavouriteMap(server), stillQueued);
